import React, { useEffect, useState } from 'react';

import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { Button, Card, CardContent, Checkbox, Fade, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, OutlinedInput, Select, TextField, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';

import { CREATE_PRODUCT_VERSION_SUCCESS, GET_PRODUCT_VERSIONS_METADATA_SUCCESS, createProductVersion, getProductVersionsMetadata } from './productVersions.actions';
import { handleToastMessage, setPageTitle } from '../layout/layout.actions';
import { isEmpty } from 'lodash';

export const FEATURE_FIELDS = [
  'amenityWheelchairAccess',
  'hasFanControl',
  'hasLightControl',
  'hasPCD',
  'hasTtlock',
  'isAppOpenable',
  'roomForStroller',
];

let ProductVersionsCreate = ({ classes, history, setPageTitle }) => {
  const [productVersion, setProductVersion] = useState();
  const [errors, setErrors] = useState([]);
  const clearErrors = () => setErrors([]);
  const [productVersionsMetadata, setProductVersionsMetadata] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();

  useEffect(async () => {
    setPageTitle('Create Product Version');
    const res = await dispatch(getProductVersionsMetadata());
    if (res.type === GET_PRODUCT_VERSIONS_METADATA_SUCCESS) {
      const { metadata } = res.response;
      setProductVersionsMetadata(metadata);
      setProductVersion({
        code: '',
        podHardwareType: null,
        locationUsageEventName : null,
        locationUsageEventType : null,
        productType: metadata.productTypes[0],
        amenityWheelchairAccess: false,
        hasFanControl: false,
        hasLightControl: false,
        hasPCD: false,
        hasTtlock: false,
        isAppOpenable: false,
        roomForStroller: false,
      });
      setIsLoading(false);
    } else {
      dispatch(handleToastMessage('Failed to load product versions metadata'));
    }
  }, []);

  // full validation is server-side, but do some limited client-side validation for quicker feedback
  const isFormValid = () => {
    return !isEmpty(productVersion.code) && !isEmpty(productVersion.podHardwareType) && !isEmpty(productVersion.productType);
  };

  const handleChange = ({ target }) => {
    let { value, name } = target;
    if (target.type === 'checkbox') {
      value = target.checked;
    }
    setProductVersion(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setSubmitting(true);
    const res = await dispatch(createProductVersion(productVersion));
    if (res.type === CREATE_PRODUCT_VERSION_SUCCESS) {
      clearErrors();
      dispatch(handleToastMessage(`Product version '${productVersion.code}' created`));
      history.push('/productVersions');
    } else {
      dispatch(handleToastMessage('Failed to create product version'));
      setErrors(res.messages || ['An unknown error occurred']);
    }
    setSubmitting(false);
  };

  return (
    <div style={{ paddingTop: 10 }}>
      {isLoading && <div className="loader" />}
      {!isLoading &&
        <Fade id="formFade" in={true}>
          <div>
            {(errors.length > 0) && (
              <div className={classes.errorContainer}>
                <Typography color="error">
                  {errors.map((e, index) => <div key={index}>{e}</div>)}
                </Typography>
              </div>
            )}
            <div id="formRowDiv" className={classes.container}>
              <Card style={{ flex: 1 }}>
                <CardContent>
                  <div className={classes.cardContent}>
                    <div>
                      <TextField
                        id={'code'}
                        name="code"
                        label={'Product Version'}
                        fullWidth
                        error={!productVersion.code}
                        value={productVersion.code}
                        onChange={handleChange}
                      />
                    </div>

                    <FormControl
                      variant="outlined"
                      fullWidth
                      error={!productVersion.productType}
                    >
                      <InputLabel shrink>Product Type</InputLabel>
                      <Select
                        aria-label="productType"
                        name="productType"
                        value={productVersion.productType}
                        onChange={handleChange}
                        input={
                          <OutlinedInput labelWidth={170} />
                        }
                      >
                        {productVersionsMetadata.productTypes.map(productType => (
                          <MenuItem key={productType} value={productType}>
                            {productType}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      variant="outlined"
                      fullWidth
                      error={!productVersion.podHardwareType}
                    >
                      <InputLabel shrink>Hardware Type</InputLabel>
                      <Select
                        aria-label="podHardwareType"
                        name="podHardwareType"
                        value={productVersion.podHardwareType}
                        onChange={handleChange}
                        input={
                          <OutlinedInput labelWidth={170} />
                        }
                      >
                        {productVersionsMetadata.hardwareTypes.map(hardwareType => (
                          <MenuItem key={hardwareType} value={hardwareType}>
                            {hardwareType}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Typography variant="h6">Features & Amenities</Typography>

                    <Grid container spacing={2}>
                      {FEATURE_FIELDS.map(property => (
                        <Grid item xs={4}>
                          <FormControlLabel
                            key={property}
                            control={<Checkbox name={property} defaultChecked={productVersion[property]} value="1" onClick={handleChange} />}
                            label={property}
                            style={{ marginTop: '-1rem' }}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    <Typography variant="h6">Location Usage Events</Typography>

                    <FormControl
                      variant="outlined"
                      fullWidth
                    >
                      <InputLabel shrink>Event Type</InputLabel>
                      <Select
                        aria-label="locationUsageEventType"
                        name="locationUsageEventType"
                        value={productVersion.locationUsageEventType}
                        onChange={handleChange}
                        input={
                          <OutlinedInput labelWidth={170} />
                        }
                      >
                        {productVersionsMetadata.locationUsageEventTypes.map(eventType => (
                          <MenuItem key={eventType} value={eventType}>
                            {eventType}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      variant="outlined"
                      fullWidth
                    >
                      <InputLabel shrink>Event Name</InputLabel>
                      <Select
                        aria-label="locationUsageEventName"
                        name="locationUsageEventName"
                        value={productVersion.locationUsageEventName}
                        onChange={handleChange}
                        input={
                          <OutlinedInput labelWidth={170} />
                        }
                      >
                        {productVersionsMetadata.locationUsageEventNames.map(eventName => (
                          <MenuItem key={eventName} value={eventName}>
                            {eventName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <div style={{ marginTop: '1rem' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ marginRight: '1rem' }}
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isFormValid()}
                      >
                        Save
                      </Button>

                      <Button
                        variant="contained"
                        onClick={() => {
                          history.push('/productVersions');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Fade>
      }
    </div>
  );
};

const styles = theme => ({
  errorContainer: {
    padding: theme.spacing(4),
    paddingBottom: 0,
  },
  cardContent: {
    gap: 14,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    margin: 10,
  },
  formControl: {
    minWidth: 75,
  },
});

ProductVersionsCreate.propTypes = {
  setPageTitle: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

const prepareForExport = compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(
    null,
    {
      setPageTitle,
    }
  ),
);

export default prepareForExport(ProductVersionsCreate);